import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Lock, CheckCircle2, History as HistoryIcon } from "lucide-react";
import HistoryDrawer, { ImportCsvButton } from "../../components/HistoryDrawer";
import { useSortedList } from "../../components/useSortedList";
import { useConfirm } from "../../components/Confirm";
import { useDataVersion } from "../../hooks/useAppShell";

const FIELDS = [
  { key: "title", label: "Title", type: "text", required: true },
  { key: "type", label: "Type", type: "select", options: ["Single","EP","Album"] },
  { key: "artist", label: "Artist", type: "artistSelect", required: true },
  { key: "release_date", label: "Planned Release Date", type: "date", hint: "Optional — leave blank if TBD" },
];
const PRE_STATUS = ["Planning","Production","Scheduled"];
const POST_STATUS = ["Released","Wrapped"];

export default function ReleasePlanning({ user }) {
  const [entries, setEntries] = useState([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [artistMap, setArtistMap] = useState({});
  const [artists, setArtists] = useState([]);
  const loc = useLocation();
  const highlightId = new URLSearchParams(loc.search).get("highlight");
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="row-release-${highlightId}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("entry-highlight"); setTimeout(() => el.classList.remove("entry-highlight"), 2600); }
    }, 350);
    return () => clearTimeout(t);
  }, [highlightId, entries]);
  const isAdmin = user.role === "admin";
  const canAdd = user.role !== "reader";

  const SORTABLE = [
    { key: "title", label: "Title" },
    { key: "type", label: "Type" },
    { key: "artist", label: "Artist" },
    { key: "release_date", label: "Planned Release Date" },
    { key: "status", label: "Status" },
  ];
  const { sorted: sortedEntries, SortBar, sortBarProps, Pagination, paginationProps } = useSortedList(entries, SORTABLE);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.entries.list("release_planning");
      setEntries(r.entries || []); setVersion(r.version || 0);
      const a = await api.entries.list("artist_database").catch((err) => { console.error("artists load failed:", err); return { entries: [] }; });
      const list = a.entries || [];
      setArtists(list);
      const m = {}; list.forEach(x => { m[x.id] = x.data?.name || x.id; });
      setArtistMap(m);
    } catch (e) { toast.error(String(e.message || e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const globalVersion = useDataVersion();
  useEffect(() => { if (globalVersion > 0) load(); }, [globalVersion, load]);

  const submit = async (data, id) => {
    try {
      if (id) await api.entries.update("release_planning", id, data, version);
      else await api.entries.create("release_planning", data);
      toast.success(id ? "Saved" : "Added");
      setShowAdd(false); setEditing(null); load();
    } catch (e) {
      if (String(e.message).includes("Someone else")) { toast.error("Refreshed — someone else made changes"); load(); }
      else toast.error(String(e.message || e));
    }
  };
  const setStatus = async (id, s) => {
    try { await api.entries.status("release_planning", id, s, version); load(); }
    catch (e) { toast.error(String(e.message || e)); load(); }
  };
  const markReleased = (id) => {
    confirm(
      "Marking this release as Released will record today as the actual release date, compute the delay vs. the planned date, and create a matching Released Songs entry. This can't be undone from the dropdown.",
      async () => {
        try {
          const r = await api.entries.markReleased(id, version);
          toast.success(
            `Released — delay ${r.planning_entry.delay_days ?? 0}d. A Released Songs entry was drafted; open it to add Spotify / Apple / YouTube links.`,
            { duration: 8000, action: { label: "Open", onClick: () => window.location.href = "/dashboard/released-songs" } }
          );
          load();
        } catch (e) {
          if (String(e.message).includes("Someone else")) { toast.error("Refreshed — please try again"); load(); }
          else toast.error(String(e.message || e));
        }
      },
      { title: "Mark as Released?", confirmLabel: "Yes, release", cancelLabel: "Not yet", tone: "cyan" }
    );
  };
  const remove = (id) => {
    confirm(
      "This release will move to Recently Deleted. You can restore it or delete it permanently within 15 days.",
      async () => {
        try { await api.entries.remove("release_planning", id); load(); toast.success("Moved to Recently Deleted"); }
        catch (e) { toast.error(String(e.message || e)); }
      },
      { title: "Delete this release?" }
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>Department</div>
          <h1 style={{ fontSize: 30, margin: "4px 0 0 0" }}>Release Planning</h1>
          <p className="muted" style={{ marginTop: 8, fontSize: 12, maxWidth: 700 }}>
            Pre-release stages are managed via the status dropdown. Moving to "Released" is only possible via the dedicated action — that records the actual release date, computes delay, and creates a matching Released Songs entry.
          </p>
        </div>
        {canAdd && (
          <div style={{ display: "flex", gap: 10 }}>
            {isAdmin && <ImportCsvButton dept="release_planning" onDone={load} />}
            <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="add-release-btn"><Plus size={14} /> New release</button>
          </div>
        )}
      </div>

      {(showAdd || editing) && (
        <Form
          initial={editing?.data || {}}
          onCancel={() => { setShowAdd(false); setEditing(null); }}
          onSubmit={(d) => submit(d, editing?.id)}
          isEdit={!!editing}
          artists={artists}
        />
      )}

      <div className="panel" style={{ overflow: "hidden" }}>
        {loading ? <div style={{ padding: 20 }} className="muted">Loading…</div> :
         entries.length === 0 ? <div style={{ padding: 40, textAlign: "center" }} className="muted">No releases yet.</div> :
        (
          <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", gap: 12 }}>
            <SortBar {...sortBarProps} />
            <span className="muted" style={{ fontSize: 11 }} data-testid="entry-count">{paginationProps.total} release{paginationProps.total === 1 ? "" : "s"}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Title</th><th>Type</th><th>Artist</th><th>Planned</th><th>Status</th><th style={{ textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map(e => {
                const s = e.data.status || "Planning";
                const isPost = POST_STATUS.includes(s);
                return (
                  <tr key={e.id} data-testid={`row-release-${e.id}`}>
                    <td style={{ fontWeight: 500 }}>{e.data.title || "—"}</td>
                    <td>{e.data.type || "—"}</td>
                    <td>{artistMap[e.data.artist] || e.data.artist || "—"}</td>
                    <td>
                      {e.data.release_date || <span className="muted">TBD</span>}
                      {e.data.actual_release_date && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>Actual: {e.data.actual_release_date} {e.data.delay_days != null && `(${e.data.delay_days > 0 ? "+" : ""}${e.data.delay_days}d)`}</div>}
                    </td>
                    <td>
                      {isPost ? (
                        <span className="badge badge-mint">{s}</span>
                      ) : isAdmin ? (
                        <select className="input" style={{ maxWidth: 160 }} value={s} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`status-${e.id}`}>
                          {PRE_STATUS.map(x => <option key={x}>{x}</option>)}
                        </select>
                      ) : (
                        <span className="badge" title="Only Admins can change status" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <Lock size={10} /> {s}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="btn" onClick={() => setHistoryFor(e.id)} data-testid={`history-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }} title="Change history"><HistoryIcon size={12} /></button>{" "}
                      {isAdmin && !isPost && (
                        <button className="btn btn-primary" onClick={() => markReleased(e.id)} data-testid={`mark-released-${e.id}`} style={{ padding: "5px 10px", fontSize: 12 }}>
                          <CheckCircle2 size={12} /> Mark released
                        </button>
                      )}{" "}
                      {isAdmin && (
                        <button className="btn" onClick={() => setEditing(e)} data-testid={`edit-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }}><Pencil size={12} /></button>
                      )}{" "}
                      {isAdmin && (
                        <button className="btn btn-danger" onClick={() => remove(e.id)} data-testid={`del-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }}><Trash2 size={12} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination {...paginationProps} />
          </>
        )}
      </div>
      {historyFor && <HistoryDrawer entryId={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}

function Form({ initial, onSubmit, onCancel, isEdit, artists = [] }) {
  const [d, setD] = useState(initial);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(d); }} className="panel" style={{ padding: 22, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ fontSize: 16 }}>{isEdit ? "Edit release" : "New release"}</h3>
        <button type="button" className="btn" onClick={onCancel}><X size={14} /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        {FIELDS.map(f => {
          if (f.type === "select") {
            return (
              <label key={f.key} style={{ fontSize: 12 }}>
                <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
                <select className="input" required={f.required} value={d[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`}>
                  <option value="">Select…</option>
                  {f.options.map(o => <option key={o}>{o}</option>)}
                </select>
              </label>
            );
          }
          if (f.type === "artistSelect") {
            return (
              <label key={f.key} style={{ fontSize: 12 }}>
                <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
                <select className="input" required={f.required} value={d[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`}>
                  <option value="">{artists.length === 0 ? "No artists yet — add via Artist Database first" : "Select artist…"}</option>
                  {artists.map(a => <option key={a.id} value={a.id}>{a.data?.name || a.id}</option>)}
                </select>
              </label>
            );
          }
          return (
            <label key={f.key} style={{ fontSize: 12 }}>
              <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
              <input className="input" type={f.type} required={f.required} value={d[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} />
              {f.hint && <span className="muted" style={{ display: "block", marginTop: 4, fontSize: 11 }} data-testid={`hint-${f.key}`}>{f.hint}</span>}
            </label>
          );
        })}
      </div>
      <div style={{ marginTop: 18, display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" data-testid="submit-entry">{isEdit ? "Save" : "Add"}</button>
      </div>
    </form>
  );
}
